import dayjs, { Dayjs } from 'dayjs'
import { ActualItemOrigin, ActualItemOrigins, actualItemOriginName } from '../../../common/constants'
import { getAdminErrorMessage } from '../adminErrorModel'
import { NotAvailable } from '../adminJobLookupModel'
import { JobOption } from '../RebuildSnapshots/rebuildSnapshotsModel'
import {
  JobWithUndatedWebEntries,
  SaveUndatedWebEntryDatesRequest,
  UndatedWebEntriesRequest,
  UndatedWebEntriesResult,
  UndatedWebEntry,
} from './undatedWebEntriesApi'

export const DateFormat = 'MM/DD/YYYY'

export type ChosenDates = Record<string, string>

export type UndatedWebEntriesFormState = {
  tenantId: number | undefined
  jobId: number | undefined
}

export const defaultJobWithUndatedWebEntries = (): JobWithUndatedWebEntries => ({
  id: 0,
  name: '',
  customerName: '',
  undatedItemCount: 0,
})

export const defaultUndatedWebEntry = (): UndatedWebEntry => ({
  itemGuid: '',
  workareaId: 0,
  workareaName: '',
  name: '',
  quantity: 0,
  units: '',
  origin: ActualItemOrigins.Web,
  lastUpdatedAt: '2026-01-01T12:00:00+00:00',
  lastVisitAt: null,
  completedAt: null,
})

export const defaultUndatedWebEntriesResult = (): UndatedWebEntriesResult => ({
  jobId: 0,
  items: [],
})

const itemCount = (count: number): string => {
  return count === 1 ? '1 undated item' : `${count} undated items`
}

export const toUndatedJobOptions = (jobs: JobWithUndatedWebEntries[]): JobOption[] => {
  return jobs.map(job => ({
    value: job.id,
    label: [`#${job.id}`, job.name, job.customerName, itemCount(job.undatedItemCount)]
      .filter(part => part !== '')
      .join(' · '),
  }))
}

export const noUndatedJobsMessage = 'No jobs on this tenant have undated web-entry items.'

export const selectedJobIdOn = (
  jobs: JobWithUndatedWebEntries[] | undefined,
  jobId: number | undefined
): number | undefined => {
  if (jobs === undefined || jobId === undefined) {
    return undefined
  }
  return jobs.some(job => job.id === jobId) ? jobId : undefined
}

export const toUndatedWebEntriesRequest = (form: UndatedWebEntriesFormState): UndatedWebEntriesRequest | null => {
  if (form.tenantId === undefined || form.jobId === undefined) {
    return null
  }
  return { tenantId: form.tenantId, jobId: form.jobId }
}

export const originLabel = (origin: ActualItemOrigin | null): string => {
  if (origin === null) {
    return 'No origin'
  }
  return actualItemOriginName(origin) ?? `Origin ${origin}`
}

export const itemLabel = (entry: UndatedWebEntry): string => {
  const workarea = entry.workareaName || `Workarea ${entry.workareaId}`
  const quantity = `${entry.quantity} ${entry.units}`.trim()
  return `${workarea} · ${entry.name} × ${quantity} · ${originLabel(entry.origin)}`
}

export const formatCandidateDate = (instant: string | null): string => {
  if (instant === null) {
    return NotAvailable
  }
  return dayjs(instant).format(DateFormat)
}

export const toLocalNoon = (value: string | Dayjs): string => {
  return dayjs(value).hour(12).minute(0).second(0).millisecond(0).toISOString()
}

export const chosenDateFor = (entry: UndatedWebEntry, chosen: ChosenDates): string => {
  return chosen[entry.itemGuid] ?? entry.lastUpdatedAt
}

export type UndatedEntryRow = UndatedWebEntry & {
  dateToSave: string
}

export const toEntryRows = (items: UndatedWebEntry[], chosen: ChosenDates): UndatedEntryRow[] => {
  return items.map(item => ({ ...item, dateToSave: chosenDateFor(item, chosen) }))
}

export const selectedGuidsOn = (items: UndatedWebEntry[], selected: string[]): string[] => {
  const present = new Set(items.map(item => item.itemGuid))
  return selected.filter(guid => present.has(guid))
}

export const toSaveDatesRequest = (
  request: UndatedWebEntriesRequest | null,
  items: UndatedWebEntry[],
  selected: string[],
  chosen: ChosenDates
): SaveUndatedWebEntryDatesRequest | null => {
  if (request === null) {
    return null
  }
  const selectedSet = new Set(selected)
  const chosenItems = items
    .filter(item => selectedSet.has(item.itemGuid))
    .map(item => ({ itemGuid: item.itemGuid, date: toLocalNoon(chosenDateFor(item, chosen)) }))
  if (chosenItems.length === 0) {
    return null
  }
  return { tenantId: request.tenantId, jobId: request.jobId, items: chosenItems }
}

export const getSavedMessage = (updatedItemCount: number): string => {
  return updatedItemCount === 1 ? 'Saved the date for 1 item.' : `Saved dates for ${updatedItemCount} items.`
}

export const getResultSummary = (result: UndatedWebEntriesResult): string => {
  if (result.items.length === 0) {
    return 'Every item on this job has a date.'
  }
  return `${itemCount(result.items.length)} on this job.`
}

export const getLoadErrorMessage = (error: unknown): string => {
  return getAdminErrorMessage(error, 'Failed to load the job\'s undated items. Please try again.')
}

export const getSaveErrorMessage = (error: unknown): string => {
  return getAdminErrorMessage(error, 'Failed to save the dates. Please try again.')
}
